import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  flex-wrap: wrap;
  @media ${desktopAndAbove} {
    gap: var(--triple-unit);
    flex-direction: row;
    flex-wrap: nowrap;
  }
`;
const Block = styled.article`
  display: flex;
  gap: var(--double-unit);
  @media ${desktopAndAbove} {
    flex-direction: column;
    width: 100%;
  }
`;
const Svg = styled.svg`
  max-width: 48px;
`;
const StarBallWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 48px;
  height: 48px;
  background: var(--c-nd-100);
  border-radius: 50%;

  & > svg {
    width: 28px;
    height: 28px;
  }
`;

const messages = defineMessages({
  title3gw: {
    defaultMessage: 'Complete 2 Game Weeks on Pro',
  },
  title3nogw: {
    defaultMessage: 'Play Twice',
  },
  description3gw: {
    defaultMessage:
      'Enter a team with your purchased cards during 2 different GW',
  },
  description3nogw: {
    defaultMessage:
      'Enter a team with your purchased cards during 2 different GW or Daily Hoops',
  },
});

type Props = {
  amount: ReactNode;
  cardsToBuy: number;
};
export const HowItWorks = ({ amount, cardsToBuy }: Props) => {
  const { sport } = useSportContext();

  const title3 = sport === Sport.NBA ? messages.title3nogw : messages.title3gw;
  const description3 =
    sport === Sport.NBA ? messages.description3nogw : messages.description3gw;

  return (
    <Root>
      <Block>
        <Svg viewBox="0 0 48 48" fill="white">
          <circle cx="24" cy="24" r="24" fillOpacity="0.11" />
          <path d="M22.8128 15.9194C24.0418 14.6904 25.7087 14 27.4467 14C31.066 14 34 16.934 34 20.5533C34 22.2913 33.3096 23.9582 32.0806 25.1872L29.5178 27.75L27.75 25.9822L30.3128 23.4194C31.073 22.6593 31.5 21.6283 31.5 20.5533C31.5 18.3147 29.6853 16.5 27.4467 16.5C26.3717 16.5 25.3407 16.927 24.5806 17.6872L22.0178 20.25L20.25 18.4822L22.8128 15.9194Z" />
          <path d="M23.4194 30.3128L25.9822 27.75L27.75 29.5178L25.1872 32.0806C23.9582 33.3096 22.2913 34 20.5533 34C16.934 34 14 31.066 14 27.4467C14 25.7087 14.6904 24.0418 15.9194 22.8128L18.4822 20.25L20.25 22.0178L17.6872 24.5806C16.927 25.3407 16.5 26.3717 16.5 27.4467C16.5 29.6853 18.3147 31.5 20.5533 31.5C21.6283 31.5 22.6593 31.073 23.4194 30.3128Z" />
          <path d="M21.1339 28.6339L28.6339 21.1339L26.8661 19.3661L19.3661 26.8661L21.1339 28.6339Z" />
        </Svg>
        <div>
          <HeadlineS as="h2">
            <FormattedMessage defaultMessage="Sign up with your link" />
            <BodyM as="p" color="var(--c-nd-400)">
              <FormattedMessage defaultMessage="Verify their email and phone number" />
            </BodyM>
          </HeadlineS>
        </div>
      </Block>
      <Block>
        <Svg viewBox="0 0 48 48" fill="white">
          <circle cx="24" cy="24" r="24" fillOpacity="0.11" />
          <path d="M25.25 15.25L26.5 14H34V21.5L32.75 22.75L29.8839 19.8839L24.2589 25.5089L22.4911 23.7411L28.1161 18.1161L25.25 15.25Z" />
          <path d="M18.375 34C20.7912 34 22.75 32.0412 22.75 29.625C22.75 27.2088 20.7912 25.25 18.375 25.25C15.9588 25.25 14 27.2088 14 29.625C14 32.0412 15.9588 34 18.375 34Z" />
        </Svg>

        <div>
          <HeadlineS as="h2">
            <FormattedMessage
              defaultMessage="Buy {cardsToBuy, plural, =1 {# card} other {# cards}} on the Primary Market"
              values={{ cardsToBuy }}
            />
            <BodyM as="p" color="var(--c-nd-400)">
              <FormattedMessage
                defaultMessage="Using <b>Auctions</b> or <b>Instant Buy</b>"
                values={{
                  b: Bold,
                }}
              />
            </BodyM>
          </HeadlineS>
        </div>
      </Block>
      <Block>
        <StarBallWrapper>
          <StarBall />
        </StarBallWrapper>
        <div>
          <HeadlineS as="h2">
            <FormattedMessage {...title3} />
            <BodyM as="p" color="var(--c-nd-400)">
              <FormattedMessage {...description3} />
            </BodyM>
          </HeadlineS>
        </div>
      </Block>
      <Block>
        <Svg viewBox="0 0 48 48" fill="white">
          <circle cx="24" cy="24" r="24" fillOpacity="0.11" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M25.25 14H22.75V15.9913C21.4333 16.1602 20.2859 16.6138 19.4294 17.3253C18.4329 18.1529 17.8457 19.3179 17.8457 20.7353V20.7575C17.8457 22.0311 18.2733 23.0386 19.1021 23.8083C19.919 24.567 21.1027 25.0733 22.58 25.4074L22.75 25.448V28.8753C22.3659 28.7724 22.0642 28.6177 21.8329 28.4246C21.4895 28.1381 21.2764 27.7469 21.189 27.25L21.1212 26.9633H17.5965V27.1549L17.6007 27.2853C17.6482 28.762 18.2812 29.9339 19.3212 30.7532C20.217 31.4587 21.4016 31.8933 22.75 32.0558V34H25.25V32.0475C26.644 31.8765 27.8614 31.4186 28.766 30.6818C29.7964 29.8426 30.4034 28.6541 30.4034 27.1753V27.1531C30.4034 25.8575 29.9758 24.8558 29.1264 24.0945C28.2915 23.3462 27.0736 22.8525 25.5339 22.5028L25.25 22.4428V19.2037C25.5478 19.3116 25.7996 19.4606 26.0023 19.6457C26.33 19.945 26.5475 20.3544 26.617 20.8857L26.6236 20.936V21.1708L30.1915 21.1585L30.1971 20.9661L30.1915 20.8339C30.1324 19.445 29.5652 18.2725 28.5981 17.4202C27.748 16.671 26.6026 16.1804 25.25 15.9924V14ZM25.25 28.9121V26.0583C25.7366 26.2169 26.0688 26.3827 26.2905 26.5661C26.5746 26.8013 26.6925 27.0782 26.6925 27.4873V27.5096C26.6925 27.9233 26.539 28.2468 26.2288 28.492C26.0019 28.6714 25.6799 28.8172 25.25 28.9121ZM22.75 19.1751V21.8056C22.4013 21.6692 22.1423 21.5216 21.9568 21.3629C21.6761 21.1228 21.5567 20.8523 21.5567 20.5124V20.4901C21.5567 20.1292 21.7002 19.8142 21.9936 19.5651C22.1788 19.4079 22.4294 19.2728 22.75 19.1751Z"
          />
        </Svg>
        <div>
          <HeadlineS as="h2">
            <FormattedMessage
              defaultMessage="You’re both rewarded with up to {amount} of credits to spend"
              values={{ amount }}
            />
            <BodyM as="p" color="var(--c-nd-400)">
              <FormattedMessage defaultMessage="The more friends you refer, the more credits you earn" />
            </BodyM>
          </HeadlineS>
        </div>
      </Block>
    </Root>
  );
};
